import isEmpty from 'lodash/isEmpty';
import { PrioritySemaphore, makePriority } from '@mimir/common/async/prioritySemaphore';
import type { ApiCallConfig, AdditionalParams, ApiClient } from '@mimir/common/util/apiClient';
import type { AuthTokenValue } from '@mimir/common/client/auth';
import { httpClientMimirJwtAuthHeaderDict } from '@mimir/common/client/auth';
import { invariant } from '@mimir/common/util/invariant';
import type { ApiGatewayClient } from 'aws-api-gateway-client';
import apiGatewayClientFactory from 'aws-api-gateway-client';
import { httpErrorStatus } from '@mimir/common/util/httpError';
import { abortableSleep } from '@mimir/common/async/abortableSleep';

const APIGATEWAY_DEFAULT_TIMEOUT_MS = 30000;

/**
 * Hooks to allow tracing of HTTP requests to AWS API Gateway.
 */
export const traceHandlers = {
  onRequest: (() => undefined) as (request: unknown) => unknown,
  onResponse: (() => undefined) as (response: unknown) => unknown,
  onError: (() => undefined) as (error: unknown) => unknown,
};

/*
  Since we specify a timeout for HTTP requests, we must also limit the number of
  concurrent in-flight requests to something sensible to give the server a fair
  chance at responding to all of the requests within the timeout duration.

  Here we limit the number of in-flight requests to 10.
*/
const semaphore = new PrioritySemaphore(10);

/*
  TODO: Remove use of global state.
*/
export async function callApi(
  clientConfig: ApiCallConfig,
  path: string,
  method: string,
  additionalParams: AdditionalParams = {},
  body: any = {},
) {
  const jwtFromGlobalState = await getJwtToken();
  return callApiWithJwtToken(jwtFromGlobalState, clientConfig, path, method, additionalParams, body);
}

export async function callApiWithJwtToken(
  jwt: string,
  clientConfig: ApiCallConfig,
  path: string,
  method: string,
  additionalParams: AdditionalParams = {},
  body: any = {},
) {
  const token: AuthTokenValue = {
    type: 'NativeJwtToken',
    jwtSecret: jwt
  };
  const apiGatewayClientConfig: any = {
    region: clientConfig.apiRegion,
    invokeUrl: path,
    host: isEmpty(clientConfig.apigHost) ? undefined : clientConfig.apigHost,
  };

  additionalParams.headers = {
    ...additionalParams.headers,
    ...httpClientMimirJwtAuthHeaderDict(token)
  };
  if (additionalParams.timeout === undefined) {
    additionalParams.timeout = APIGATEWAY_DEFAULT_TIMEOUT_MS;
  }

  const apiGatewayClient = apiGatewayClientFactory.newClient(
    apiGatewayClientConfig,
  );

  const info = { method, path, params: additionalParams.queryParams, data: body };
  traceHandlers.onRequest(info);

  return await callWithApiGatewayClient(apiGatewayClient, info, additionalParams);
}

interface RequestInfo {
  method: string;
  path: string;
  params?: Record<string, unknown>;
  data?: string;
}

async function callWithApiGatewayClient(apiGatewayClient: ApiGatewayClient, reqInfo: RequestInfo & Record<string, unknown>, additionalParams: AdditionalParams) {
  const priority = additionalParams.priority ?? makePriority();
  const cancelToken = additionalParams.cancelToken ?? null;
  let tries = 0;
  let retryBlocker: string | undefined;
  while (true) {
    try {
      const result = await semaphore.limit(async () => {
        const response = await apiGatewayClient.invokeApi(
          {},
          '',
          reqInfo.method as 'GET' | 'POST' | 'DELETE' | 'UPDATE',
          additionalParams,
          reqInfo.data,
        );
        traceHandlers.onResponse(response);
        return response;
      }, priority, cancelToken);
      if (tries > 0) {
        console.log('Call ' + reqInfo.path + ' succeeded after ' + tries + ' tries.');
      }
      return result;
    } catch (err) {
      ++tries;
      if (additionalParams.signal != null) {
        const retryInfo = getRetryInfo(reqInfo.method, err);
        if (retryInfo.canRetry) {
          // eslint-disable-next-line no-bitwise
          const delay = 100 * (1 << tries);
          console.warn('Will retry API call ' + reqInfo.path + ' in ' + delay + ' msec, due to ' + retryInfo.reason);
          try {
            await abortableSleep(delay, additionalParams.signal);
            continue;
          } catch(_) {
            /*
              Ignore sleep cancellation in favor of reporting the original
              error below.
            */
          }
        } else {
          retryBlocker = retryInfo.reason;
        }
      } else {
        retryBlocker = 'no abort signal to bound try attempts';
      }

      traceHandlers.onError(err);

      reqInfo['tries'] = tries;
      reqInfo['retryBlocker'] = retryBlocker;
      const errWithInfo = err as { info?: Record<string, unknown> };
      if (errWithInfo.info) {
        Object.assign(reqInfo, errWithInfo.info);
      }

      errWithInfo.info = reqInfo;
      throw errWithInfo;
    }
  }
}

interface RetryInfo {
  canRetry: boolean;
  reason: string;
}

function getRetryInfo(method: string, error: unknown): RetryInfo {
  const code = httpErrorStatus(error);
  if (code === 429) {
    return { canRetry: true, reason: 'rate limited request' };
  }

  if (code === 503) {
    if ((method === 'GET' || method === 'PUT')) {
      return { canRetry: true, reason: 'service unavailable for idempotent method' };
    }
    return { canRetry: false, reason: 'method not idempotent' };
  }

  return { canRetry: false, reason: 'HTTP code not considered retryable' };
}

/*
  Warning: This function uses global state. Make sure that
  provideGlobalJwtTokenProvider() has been called before trying to call this.
*/
export function apiClient(staticConfig: ApiCallConfig): ApiClient {
  const provider = getGlobalJwtTokenProvider();
  return {
    call: callApi,
    config: () => staticConfig,
    refreshAuth: () => provider.refresh()
  };
}

export function dynamicApiClient(config: () => ApiCallConfig): ApiClient {
  return {
    call: callApi,
    config,
    refreshAuth: () => getGlobalJwtTokenProvider().refresh()
  };
}

export interface JwtTokenProvider {
  getJwtToken(): Promise<string>;
  refresh(): Promise<void>;
}

let globalJwtTokenProvider: JwtTokenProvider | null = null;

export function installGlobalJwtTokenProvider(provider: JwtTokenProvider, silent = false) {
  if (globalJwtTokenProvider != null && !silent) {
    /*
      TODO: This kind of trampling on global state is error prone and should be
      avoided.
    */
    console.warn('Overriding previously installed token provider.');
  }
  globalJwtTokenProvider = provider;
}

function getGlobalJwtTokenProvider() {
  invariant(globalJwtTokenProvider != null, 'Expected installGlobalJwtTokenProvider() to already have been called.');
  return globalJwtTokenProvider;
}

export const getJwtToken = async () => {
  return getGlobalJwtTokenProvider().getJwtToken();
};
